import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PublicHeader from '../Common/Layout/PublicHeader';
import { rexTechTheme } from '../Common/Theme';
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  Shield,
  ArrowRight,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

// Import your API configuration
import config from '../Common/Configurations/APIConfig';

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setApiError('');
    setErrors({});
    
    // Validation
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username)) {
      newErrors.username = 'Please enter a valid email address';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }
    
    try {
      // Prepare login payload
      const loginPayload = {
        username: formData.username,
        password: formData.password
      };
      
      // Call login API
      const response = await axios.post(
        `${config.APIACTIVEURL}${config.LOGIN}`,
        loginPayload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Login response:', response.data);
      
      if (response.data.success) {
        // Store token and user data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userId', response.data.userId);
      
        
        // Store expiration time if needed
        const expirationTime = new Date(response.data.expiration).getTime();
        localStorage.setItem('tokenExpiration', expirationTime.toString());
        
        // If remember me is checked, store credentials securely
        if (formData.rememberMe) {
          // Note: In production, use more secure methods like httpOnly cookies
          localStorage.setItem('rememberedEmail', formData.username);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        setSuccessMessage('Login successful! Redirecting...');
        
        // Redirect based on role
       // Redirect based on role
setTimeout(() => {
  const role = response.data.roleName?.toLowerCase();
  
  // Handle COMPANYUSER and SuperAdmin both redirecting to admin dashboard
  if (role === 'companyuser' || role === 'superadmin') {
    navigate('/admin/dashboard');
  } else {
    // Fallback to default redirection for other roles
    switch(role) {
      case 'clientuser':
        navigate('/customer/requests');
        break;
      case 'staff':
        navigate('/staff/dashboard');
        break;
      default:
        navigate('/');
    }
  }
}, 1500);
        
      } else if (response.data.registrationPending) {
        // Handle pending activation
        setApiError('Your account is pending activation. Please contact support.');
      } else {
        // Handle other errors from API
        setApiError(response.data.message || 'Login failed. Please try again.');
      }
      
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorData = error.response.data;
        
        if (error.response.status === 401) {
          setApiError('Invalid email or password. Please try again.');
        } else if (error.response.status === 403) {
          setApiError('Account is not active. Please contact support.');
        } else if (error.response.status === 429) {
          setApiError('Too many login attempts. Please try again later.');
        } else {
          setApiError(errorData.message || 'Login failed. Please try again.');
        }
      } else if (error.request) {
        // Request made but no response received
        setApiError('Network error. Please check your internet connection.');
      } else {
        // Something else went wrong
        setApiError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Check for remembered email on component mount
  React.useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({
        ...prev,
        username: rememberedEmail,
        rememberMe: true
      }));
    }
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: rexTechTheme.colors.neutral.lightGray,
      fontFamily: rexTechTheme.typography.fontFamily
    }}>
      {/* Header */}
      <PublicHeader />
      
      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: rexTechTheme.spacing.xl,
        paddingTop: rexTechTheme.spacing.xxl,
        '@media (max-width: 768px)': {
          padding: rexTechTheme.spacing.md,
          paddingTop: rexTechTheme.spacing.xl
        }
      }}>
        <div style={{
          width: '100%',
          maxWidth: '450px',
          minHeight: '500px',
          backgroundColor: rexTechTheme.colors.neutral.white,
          padding: rexTechTheme.spacing.xxl,
          borderRadius: rexTechTheme.borderRadius.xlarge,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          '@media (max-width: 768px)': {
            padding: rexTechTheme.spacing.xl
          }
        }}>
          {/* Form Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: rexTechTheme.spacing.xl
          }}>
            <h1 style={{
              margin: `0 0 ${rexTechTheme.spacing.md} 0`,
              fontSize: rexTechTheme.typography.fontSize.xxlarge,
              color: rexTechTheme.colors.primary.dark,
              fontWeight: 700
            }}>
              Sign In
            </h1>
            <p style={{
              margin: 0,
              color: rexTechTheme.colors.neutral.darkGray,
              fontSize: rexTechTheme.typography.fontSize.regular
            }}>
              Enter your credentials to access your account
            </p>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div style={{
              padding: rexTechTheme.spacing.md,
              backgroundColor: rexTechTheme.colors.primary.lightest,
              border: `1px solid ${rexTechTheme.colors.primary.light}`,
              borderRadius: rexTechTheme.borderRadius.medium,
              color: rexTechTheme.colors.primary.dark,
              marginBottom: rexTechTheme.spacing.lg,
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.sm
            }}>
              <CheckCircle size={18} />
              {successMessage}
            </div>
          )}

          {/* API Error Message */}
          {apiError && (
            <div style={{
              padding: rexTechTheme.spacing.md,
              backgroundColor: rexTechTheme.colors.accent.lightest,
              border: `1px solid ${rexTechTheme.colors.accent.orange}`,
              borderRadius: rexTechTheme.borderRadius.medium,
              color: rexTechTheme.colors.accent.orange,
              marginBottom: rexTechTheme.spacing.lg,
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.sm
            }}>
              <AlertCircle size={18} />
              {apiError}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            {/* Email/Username Field */}
            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
              <label style={{
                display: 'block',
                marginBottom: rexTechTheme.spacing.xs,
                color: rexTechTheme.colors.primary.dark,
                fontWeight: 600,
                fontSize: rexTechTheme.typography.fontSize.regular
              }}>
                Email Address
              </label>
              <div style={{
                position: 'relative'
              }}>
                <Mail 
                  size={20} 
                  style={{
                    position: 'absolute',
                    left: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: rexTechTheme.colors.neutral.darkGray,
                    pointerEvents: 'none'
                  }}
                />
                <input
                  type="email"
                  name="username"
                  value={formData.username}
                  onChange={(e) => {
                    setFormData({...formData, username: e.target.value});
                    if (errors.username) setErrors({...errors, username: ''});
                    setApiError('');
                  }}
                  style={{
                    width: '100%',
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} 48px`,
                    border: `2px solid ${errors.username ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    ':focus': {
                      borderColor: rexTechTheme.colors.primary.main,
                      boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                    }
                  }}
                  placeholder="you@example.com"
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>
              {errors.username && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.xs,
                  marginTop: rexTechTheme.spacing.xs,
                  color: rexTechTheme.colors.accent.orange,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  <AlertCircle size={14} />
                  <span>{errors.username}</span>
                </div>
              )}
            </div>

            {/* Password Field */}
            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: rexTechTheme.spacing.xs
              }}>
                <label style={{
                  color: rexTechTheme.colors.primary.dark,
                  fontWeight: 600,
                  fontSize: rexTechTheme.typography.fontSize.regular
                }}>
                  Password
                </label>
                {/* <Link 
                  to="/forgot-password"
                  style={{
                    color: rexTechTheme.colors.primary.main,
                    textDecoration: 'none',
                    fontSize: rexTechTheme.typography.fontSize.small,
                    ':hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Forgot password?
                </Link> */}
              </div>
              <div style={{
                position: 'relative'
              }}>
                <Lock 
                  size={20} 
                  style={{
                    position: 'absolute',
                    left: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: rexTechTheme.colors.neutral.darkGray,
                    pointerEvents: 'none'
                  }}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({...formData, password: e.target.value});
                    if (errors.password) setErrors({...errors, password: ''});
                    setApiError('');
                  }}
                  style={{
                    width: '100%',
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} 48px`,
                    border: `2px solid ${errors.password ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    boxSizing: 'border-box',
                    ':focus': {
                      borderColor: rexTechTheme.colors.primary.main,
                      boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                    }
                  }}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: rexTechTheme.spacing.md,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: rexTechTheme.colors.neutral.darkGray,
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ':hover': {
                      color: rexTechTheme.colors.primary.main
                    }
                  }}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rexTechTheme.spacing.xs,
                  marginTop: rexTechTheme.spacing.xs,
                  color: rexTechTheme.colors.accent.orange,
                  fontSize: rexTechTheme.typography.fontSize.small
                }}>
                  <AlertCircle size={14} />
                  <span>{errors.password}</span>
                </div>
              )}
            </div>

            {/* Remember Me */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: rexTechTheme.spacing.xl
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm,
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={(e) => setFormData({...formData, rememberMe: e.target.checked})}
                  style={{
                    width: '18px',
                    height: '18px',
                    accentColor: rexTechTheme.colors.primary.main,
                    cursor: 'pointer'
                  }}
                  disabled={isLoading}
                />
                <span style={{
                  color: rexTechTheme.colors.neutral.darkGray,
                  fontSize: rexTechTheme.typography.fontSize.regular
                }}>
                  Remember me
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: rexTechTheme.spacing.lg,
                background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                color: rexTechTheme.colors.neutral.white,
                border: 'none',
                borderRadius: rexTechTheme.borderRadius.medium,
                fontSize: rexTechTheme.typography.fontSize.regular,
                fontWeight: 600,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: rexTechTheme.spacing.sm,
                opacity: isLoading ? 0.7 : 1,
                boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                ':hover': {
                  background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                  transform: isLoading ? 'none' : 'translateY(-2px)',
                  boxShadow: isLoading ? '0 4px 15px rgba(30, 132, 73, 0.3)' : '0 6px 20px rgba(30, 132, 73, 0.4)'
                }
              }}
            >
              {isLoading ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                  Signing In...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <div style={{
            textAlign: 'center',
            padding: rexTechTheme.spacing.lg,
            backgroundColor: rexTechTheme.colors.primary.lightest,
            borderRadius: rexTechTheme.borderRadius.medium,
            border: `1px solid ${rexTechTheme.colors.primary.lighter}`,
            marginTop: rexTechTheme.spacing.xl
          }}>
            <p style={{
              margin: 0,
              color: rexTechTheme.colors.primary.dark,
              fontSize: rexTechTheme.typography.fontSize.regular
            }}>
              New to RexTech?{' '}
              <Link 
                to="/register"
                style={{
                  color: rexTechTheme.colors.primary.main,
                  textDecoration: 'none',
                  fontWeight: 600,
                  ':hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
              Join as Customer
              </Link>
            </p>
          </div>

          {/* Security Notice */}
          <div style={{
            marginTop: rexTechTheme.spacing.lg,
            textAlign: 'center'
          }}>
            <p style={{
              margin: 0,
              fontSize: rexTechTheme.typography.fontSize.xs,
              color: rexTechTheme.colors.neutral.darkGray,
              lineHeight: 1.5
            }}>
              <Shield size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Your data is protected with enterprise-grade security.
              <br />
              By signing in, you agree to our{' '}
              <Link 
                to="/terms" 
                style={{
                  color: rexTechTheme.colors.primary.main,
                  textDecoration: 'none'
                }}
              >
                Terms
              </Link>{' '}
              and{' '}
              <Link 
                to="/privacy" 
                style={{
                  color: rexTechTheme.colors.primary.main,
                  textDecoration: 'none'
                }}
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        backgroundColor: rexTechTheme.colors.neutral.white,
        padding: rexTechTheme.spacing.lg,
        borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`,
        textAlign: 'center',
        marginTop: 'auto'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          color: rexTechTheme.colors.neutral.darkGray,
          fontSize: rexTechTheme.typography.fontSize.small
        }}>
          <p style={{ margin: 0 }}>
            © 2024 RexTech. Creating a new home for our planet through sustainable technology.
          </p>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: rexTechTheme.spacing.lg,
            marginTop: rexTechTheme.spacing.sm,
            flexWrap: 'wrap'
          }}>
            <Link 
              to="/privacy" 
              style={{
                color: rexTechTheme.colors.neutral.darkGray,
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  color: rexTechTheme.colors.primary.main
                }
              }}
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms" 
              style={{
                color: rexTechTheme.colors.neutral.darkGray,
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  color: rexTechTheme.colors.primary.main
                }
              }}
            >
              Terms of Service
            </Link>
            <Link 
              to="/contact" 
              style={{
                color: rexTechTheme.colors.neutral.darkGray,
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  color: rexTechTheme.colors.primary.main
                }
              }}
            >
              Contact Support
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;